import { useEffect, useLayoutEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import MessagesList from 'ageas-react-component-message-list'

import Progress from 'ageas-react-component-progressbar'
import WarningBar from 'ageas-react-component-system-notification'
import { cloneDeep } from 'lodash'

import { createPortal } from 'react-dom'
import { SubHeader } from '../Components/SubHeaderActions/SubHeader'
import { useProgressStore } from '../Stores/progressStore'
import { useReportStore } from '../Stores/reportStore'
import { useSimulatorStore } from '../Stores/simulatorStore'
import { useAgent } from '../Stores/useAgent'
import { useCreateProposalStore } from '../Stores/useCreateProposalStore'
import { useRunPetisSimulationStore } from '../Stores/useRunPetisSimulationStore'
import { useSubmitProposalStore } from '../Stores/useSubmitProposalStore'
import { useWarningStore } from '../Stores/warningStore'
import { DotProgress } from '../Types/dots'
import { PAGE_NAME } from '../constants/pageName'
import { ROUTES } from '../constants/routes'
import { SESSION_STORAGE } from '../constants/sessionStorage'
import { WARNING_BAR } from '../constants/warningBar'
import { useChangedSimulationNumber } from '../hooks/useChangedSimulationNumber'
import { useReferrals } from '../hooks/useReferrals'
import { ProgressBarContainer, SimulationContainer } from './styles'

const defaultDotsProgressData: DotProgress[] = [
  {
    active: true,
    name: PAGE_NAME.simulator,
    pageName: ROUTES.base,
    id: 1,
    onValueChanged: () => null,
    key: 'KEY1',
    tooltip: PAGE_NAME.simulator,
    lastItemSaved: false,
    indexAbove: false,
  },
  {
    active: false,
    name: PAGE_NAME.simulationResult,
    pageName: ROUTES.simulationResult,
    id: 2,
    onValueChanged: () => null,
    key: 'KEY2',
    tooltip: PAGE_NAME.simulationResult,
    lastItemSaved: false,
    indexAbove: false,
  },
  {
    active: false,
    name: PAGE_NAME.ownerData,
    pageName: ROUTES.ownerData,
    id: 3,
    onValueChanged: () => null,
    key: 'KEY3',
    tooltip: PAGE_NAME.ownerData,
    lastItemSaved: false,
    indexAbove: false,
  },
  {
    active: false,
    name: PAGE_NAME.additionalRiskData,
    pageName: ROUTES.additionalRiskData,
    id: 4,
    onValueChanged: () => null,
    key: 'KEY4',
    tooltip: PAGE_NAME.additionalRiskData,
    lastItemSaved: false,
    indexAbove: false,
  },
  {
    active: false,
    name: PAGE_NAME.summaryConditions,
    pageName: ROUTES.summaryConditions,
    id: 5,
    onValueChanged: () => null,
    key: 'KEY5',
    tooltip: PAGE_NAME.summaryConditions,
    lastItemSaved: false,
    indexAbove: false,
  },
  {
    active: false,
    name: PAGE_NAME.formalization,
    pageName: ROUTES.formalization,
    id: 6,
    onValueChanged: () => null,
    key: 'KEY6',
    tooltip: PAGE_NAME.formalization,
    lastItemSaved: false,
    indexAbove: false,
  },
  {
    active: false,
    name: PAGE_NAME.confirmation,
    pageName: ROUTES.confirmation,
    id: 7,
    onValueChanged: () => null,
    key: 'KEY7',
    tooltip: PAGE_NAME.confirmation,
    lastItemSaved: false,
    indexAbove: false,
  },
]

const elementPortal = document.getElementsByTagName('body')?.[0]

const POLICY_SUB_STATUS_BOO = 'BOO'

export const BaseLayout = () => {
  const { isReferral } = useRunPetisSimulationStore()
  const { submitProposal } = useSubmitProposalStore()
  const { handleReferralsMessages } = useReferrals()
  const [oldStep, setOldStep] = useState<number>(1)
  const [lastStep, setLastStep] = useState<number>(7)
  const { hasCreatedProposal } = useCreateProposalStore()
  const location = useLocation()
  const [dotsProgressData, setDotsProgressData] = useState<DotProgress[]>(
    defaultDotsProgressData,
  )
  const { setAgentBySessionStorage } = useAgent()
  const { options: warningBarConfiguration, setWarningOptions } =
    useWarningStore()

  useChangedSimulationNumber()

  const {
    resetTotalPricesObjects,
    isFullStop,
    setIsNPANavigationClientContext,
    setNPANavigationClientContext,
    setIsNPALeadsContext,
    setNPALeadsContext
  } = useSimulatorStore()

  const navigate = useNavigate()

  const {
    currentStep,
    stepsNavigated,
    routesNavigated,
    setRoutesNavigated,
    setStepsNavigated,
  } = useProgressStore()

  const { isReportGeneratedEnabled } = useReportStore()

  const mapToPageNumber = () => {
    const mapped: { [key: string]: number } = {}

    defaultDotsProgressData.forEach((dotProgress) => {
      mapped[dotProgress.pageName] = dotProgress.id
    })

    return mapped
  }

  const mappedPageNumber = mapToPageNumber()

  const handleCloseWarningBar = () => {
    setWarningOptions({
      labelTypes: [],
      mode: WARNING_BAR.mode.hide,
      modal: false,
    })
  }

  const handleNavigation = (route: string) => {
    if (submitProposal?.policySubStatus === POLICY_SUB_STATUS_BOO) {
      return
    }

    if (
      (process.env.NODE_ENV === 'development' ||
        routesNavigated.includes(route)) &&
      (route === '/' || mappedPageNumber[route] <= lastStep + 1)
    ) {
      if (ROUTES.simulationResult === route) {
        resetTotalPricesObjects()
      }

      navigate(route)
    } else {
      console.warn('Rota não permitida', mappedPageNumber[route] <= lastStep)
    }
  }

  useLayoutEffect(() => {
    const contextSelectedArray: any = sessionStorage.getItem(
      SESSION_STORAGE.contextSelectedArray,
    )

    setAgentBySessionStorage(JSON.parse(contextSelectedArray))
  }, [setAgentBySessionStorage])

  useEffect(() => {
    const _dotsProgressData = cloneDeep(dotsProgressData)

    _dotsProgressData.forEach((item) => {
      item.active = stepsNavigated.includes(item.id)
      item.lastItemSaved = stepsNavigated.includes(item.id)
    })

    setDotsProgressData(_dotsProgressData)
  }, [currentStep, stepsNavigated])

  useEffect(() => {
    if (currentStep > lastStep) {
      setLastStep(currentStep)
    }

    if (currentStep > oldStep) {
      const nextButton: HTMLElement = document.querySelector(
        '.progress-petis-container .flickity-button.flickity-prev-next-button.next',
      ) as HTMLElement
      nextButton && nextButton.click()
    }

    setOldStep(currentStep)
    setStepsNavigated(currentStep)
  }, [currentStep])

  useEffect(() => {
    setRoutesNavigated(location.pathname)
  }, [currentStep, location, setRoutesNavigated])

  useEffect(() => {
    if (isReferral && currentStep !== 2) {
      const styleElement = document.createElement('style')

      const cssStyles = `
      .paddingReferral {
        padding-bottom: 60px !important;
      }
      `
      styleElement.innerHTML = cssStyles
      document.head.appendChild(styleElement)

      document.body.classList.add('paddingReferral')
    } else if (currentStep === 2) {
      document.body.classList.remove('paddingReferral')
    }
  }, [currentStep, isReferral])

  useEffect(() => {
    const NPANavigationClientContext = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE.NPANavigationClientContext) ||
        '{}',
    )

    if (Object.values(NPANavigationClientContext).length) {
      setIsNPANavigationClientContext(true)
      setNPANavigationClientContext(NPANavigationClientContext)
    }
  }, [])


  useEffect(() => {
    // New leadsContext -  NPA_LeadsContext
    const leadsContext = JSON.parse(sessionStorage.getItem(SESSION_STORAGE.NPALeadsContext) ||
        '{}',
    )
    const leadId =  leadsContext?.context?.lead_id || '';
    const client_nif = leadsContext?.context?.client_nif || '';

    if (Object.values(leadsContext).length && leadsContext) {
        setIsNPALeadsContext(true)
        setNPALeadsContext({leadId, client_nif})
    }
  }, [])

  useEffect(() => {
    return () => {
      sessionStorage.removeItem(SESSION_STORAGE.oldRpsQuoteId)
      sessionStorage.removeItem(SESSION_STORAGE.rpsQuoteId)
    }
  }, [])

  return (
    <>
      <SimulationContainer isFullStop={isFullStop}>
        {(!isReportGeneratedEnabled || hasCreatedProposal) && <SubHeader />}
        <div className="progress-petis-container">
          <ProgressBarContainer>
            <Progress
              Bar
              version="2"
              hidden={false}
              colorTheme="blue"
              onValueChanged={(id: number, route: string) => {
                handleNavigation(route)
              }}
              items={dotsProgressData}
            />
          </ProgressBarContainer>
        </div>
        <Outlet />

        {isReferral && currentStep !== 2 && (
          <MessagesList
            id="referralsMessage"
            type="error"
            list={handleReferralsMessages()}
            version="2"
          />
        )}

        {createPortal(
          <>
            <WarningBar
              version="2"
              mode={warningBarConfiguration.mode}
              defaultWarning={warningBarConfiguration.defaultWarning}
              labelTypes={warningBarConfiguration.labelTypes}
              linkLabel={warningBarConfiguration.linkLabel}
              modal={warningBarConfiguration.modal}
              onValueChanged={handleCloseWarningBar}
            />
          </>,
          elementPortal!,
        )}
      </SimulationContainer>
    </>
  )
}
